import React, { useCallback, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";

import { IPD_COLORS, IPD_SHAPE, IpdButton } from "../../ipd/utils";
import GenerateAbha from "../../hlthtek-demo/components/GenerateAbha";
import VerifyAbha from "../../hlthtek-demo/components/VerifyAbha";

/**
 * The Aadhaar/ABHA one-touch fast-path for emergency registration (ticket 27).
 *
 * This reuses the hospital's existing ABDM registration flow verbatim — the same
 * `GenerateAbha` / `VerifyAbha` components IPD (`AbhaPanel`) and OPD use — rather
 * than inventing a second integration. On a successful fetch the ABDM flow has
 * already created-or-matched the `PatientRecord` server-side (via
 * `create_patient_record`, which dedups on Aadhaar/ABHA within the facility), so
 * the result carries a `patient_id`; the registration then simply attaches the
 * visit to it.
 *
 * It is a convenience, never a gate: the whole panel is skippable and the manual
 * fields below it stay fully usable. When ABDM is not enabled for the facility
 * the panel steps aside with a one-line note instead of showing dead buttons.
 */

const CURRENT_YEAR = new Date().getFullYear();

const GENDER_CODE = { M: "M", MALE: "M", F: "F", FEMALE: "F" };

/** ABHA profile / matched-patient row -> the emergency form's own fields. */
function mapAbhaToEd(details) {
  if (!details || typeof details !== "object") return {};

  const name =
    details.fullName ||
    details.name ||
    `${details.firstName || details.first_name || ""} ${details.lastName || details.last_name || ""}`.trim();

  const genderRaw = String(details.gender || "").toUpperCase();
  const gender = GENDER_CODE[genderRaw] || (genderRaw ? "T" : "");

  // The form records age, not date of birth — derive an approximate age from the
  // year of birth the profile carries.
  let age = "";
  const year =
    details.yearOfBirth ||
    (details.dob ? String(details.dob).slice(0, 4) : "") ||
    "";
  if (/^\d{4}$/.test(String(year))) {
    age = String(Math.max(0, CURRENT_YEAR - Number(year)));
  }

  const abhaNumber =
    details.healthIdNumber || details.ABHANumber || details.abhaNumber || details.abha_no || "";
  const abhaAddress =
    details.healthId || details.preferredAbhaAddress || details.abhaAddress || details.abha_id || "";

  const patientId = details.patient_id || "";

  const mapped = {};
  if (name) mapped.name = name;
  if (gender) mapped.gender = gender;
  if (age) mapped.age = age;
  if (abhaNumber) mapped.abhaNumber = abhaNumber;
  if (abhaAddress) mapped.abhaAddress = abhaAddress;
  if (patientId) mapped.patientId = patientId;
  return mapped;
}

export default function EmergencyAbhaPanel({ onApply, taggingPatientId, disabled }) {
  const [genOpen, setGenOpen] = useState(false);
  const [verOpen, setVerOpen] = useState(false);
  const [isAbdmEnabled, setIsAbdmEnabled] = useState(false);
  const [healthIdDetails, setHealthIdDetails] = useState(null);
  const [partialMatch, setPartialMatch] = useState(null);
  const [, setMultiple] = useState([]);
  const [, setTxnId] = useState(null);

  useEffect(() => {
    const flag = sessionStorage.getItem("isAbdmEnabled");
    try {
      setIsAbdmEnabled(flag != null ? JSON.parse(flag) : false);
    } catch {
      setIsAbdmEnabled(false);
    }
  }, []);

  const apply = useCallback(
    (details) => {
      const mapped = mapAbhaToEd(details);
      if (Object.keys(mapped).length) onApply(mapped);
    },
    [onApply]
  );

  useEffect(() => {
    if (healthIdDetails) apply(healthIdDetails);
  }, [healthIdDetails, apply]);

  useEffect(() => {
    if (partialMatch) apply(partialMatch);
  }, [partialMatch, apply]);

  const handleSetPatientData = useCallback(
    (legacy) => {
      if (legacy) apply(legacy);
    },
    [apply]
  );

  const noop = useCallback(() => {}, []);

  if (!isAbdmEnabled) {
    return (
      <Box
        sx={{
          mb: 2,
          px: 1.75,
          py: 1.25,
          borderRadius: `${IPD_SHAPE.radiusMd}px`,
          bgcolor: IPD_COLORS.pageBg,
          border: `1px dashed ${IPD_COLORS.border}`,
        }}
      >
        <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary }}>
          Aadhaar/ABHA fetch is not enabled for this facility — enter the patient's details below.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mb: 2.5,
        p: 2,
        borderRadius: `${IPD_SHAPE.radiusMd}px`,
        bgcolor: IPD_COLORS.info.bg,
        border: `1px solid ${IPD_COLORS.info.border}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 1.5,
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: IPD_COLORS.textPrimary }}>
            Fetch from Aadhaar / ABHA
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary }}>
            Optional — pull the patient's details in one touch, or skip and type them below.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1.25, flexShrink: 0 }}>
          <IpdButton
            variant="outlined"
            size="small"
            startIcon={<VerifiedUserIcon />}
            onClick={() => setVerOpen(true)}
            disabled={disabled}
          >
            Verify ABHA
          </IpdButton>
          <IpdButton
            size="small"
            startIcon={<AutoAwesomeIcon />}
            onClick={() => setGenOpen(true)}
            disabled={disabled}
          >
            Generate ABHA
          </IpdButton>
        </Box>
      </Box>

      <GenerateAbha
        showGenerateModal={genOpen}
        setShowGenerateModal={setGenOpen}
        setHealthIdDetails={setHealthIdDetails}
        fetchPatientDetails={noop}
        taggingPatientId={taggingPatientId}
        setMultiplePatientData={setMultiple}
        setSelectedPartiallyMatchedPatient={setPartialMatch}
        setPatientData={handleSetPatientData}
        resetForm={noop}
        setTransactionIdForDataVerification={setTxnId}
        onClose={noop}
      />

      <VerifyAbha
        showVerifyModal={verOpen}
        setShowVerifyModal={setVerOpen}
        setHealthIdDetails={setHealthIdDetails}
        fetchPatientDetails={noop}
        taggingPatientId={taggingPatientId}
        setMultiplePatientData={setMultiple}
        setSelectedPartiallyMatchedPatient={setPartialMatch}
        setPatientData={handleSetPatientData}
        resetForm={noop}
        setTransactionIdForDataVerification={setTxnId}
        onNavigateToGenerateAbha={() => setGenOpen(true)}
        onClose={noop}
      />
    </Box>
  );
}
